import React, { useMemo, useState, useEffect, useRef } from 'react';
import { procesarFormulario } from '../../../admin/form-master/utils/gruposSubgruposProcessor';
import { MENU_CONFIG, shouldFilterGroupByName, FORM_CONFIG, GROUP_LABEL_OVERRIDES, GROUP_PRESELECTION_CONFIG, QUESTION_LABEL_OVERRIDES, QUESTION_PRESELECTION_CONFIG } from '../config/config.js';
import './QuestionSelectorTree.css';

const QuestionSelectorTree = ({ questions, selectedQuestionIds, onSelectionChange }) => {
  // Process questions into a tree structure with filtering
  const treeData = useMemo(() => {
    if (!questions || questions.length === 0) return [];
    
    // Process questions into groups
    const result = procesarFormulario(questions);
    const grupos = result.grupos || [];
    
    // Recursive filtering function
    const filterNodes = (nodes) => {
      return nodes.reduce((acc, node) => {
        // Clone node to avoid mutation
        const newNode = { ...node };
        const question = newNode.preguntaOriginal || newNode;
        const type = question.type || newNode.tipo;
        
        // 1. Filter Groups
        if (newNode.tipo === 'grupo' || newNode.tipo === 'subgrupo' || type === 'begin_group') {
           // Check if group should be filtered by name
           if (shouldFilterGroupByName(newNode)) {
               return acc; // Skip this group entirely
           }
           
           // Recursively filter children
           if (newNode.gruposHijos) newNode.gruposHijos = filterNodes(newNode.gruposHijos);
           if (newNode.subgrupos) newNode.subgrupos = filterNodes(newNode.subgrupos);
           if (newNode.preguntas) newNode.preguntas = filterNodes(newNode.preguntas);
           
           // Optional: Hide empty groups if desired, but user didn't explicitly ask for it.
           // However, if a group becomes empty due to filtering all children, it might be confusing.
           // For now, we keep it to show structure unless empty groups are confusing.
           
           acc.push(newNode);
        } 
        // 2. Filter Questions
        else if (newNode.tipo === 'pregunta' || !newNode.tipo) { 
             const configRptTabla = question.config_rpt_tabla ?? newNode.config_rpt_tabla ?? null;
             const configRptColumna = question.config_rpt_columna ?? newNode.config_rpt_columna ?? null;
             const hasValue = (value) => value !== null && value !== undefined && String(value).trim() !== '';
             const tieneTabla = hasValue(configRptTabla);
             const tieneColumna = hasValue(configRptColumna);
             if (!tieneTabla || !tieneColumna) {
                 return acc;
             }
             
             // Check exclusion by type
             if (FORM_CONFIG.search.excludeSearchByType && FORM_CONFIG.search.excludeSearchByType.includes(type)) {
                 return acc;
             }
             
             // Check exclusion by name
            if (FORM_CONFIG.search.excludeSearchByName && FORM_CONFIG.search.excludeSearchByName.length > 0) {
                const cfg = FORM_CONFIG.search.excludeSearchByNameConfig || {};
                const enabled = cfg.enabled !== false;
                if (enabled) {
                    const exactMatch = cfg.exactMatch !== false;
                    const caseSensitive = cfg.caseSensitive !== false;
                    const questionName = question.name;

                    const isExcluded = FORM_CONFIG.search.excludeSearchByName.some((excludedName) => {
                        if (excludedName === null || excludedName === undefined) return false;
                        if (questionName === null || questionName === undefined) return false;

                        const a = caseSensitive ? String(questionName) : String(questionName).toLowerCase();
                        const b = caseSensitive ? String(excludedName) : String(excludedName).toLowerCase();

                        if (exactMatch) return a === b;
                        return a.includes(b);
                    });

                    if (isExcluded) return acc;
                }
            }
             
             // Check exclusion by label (partial match)
             if (FORM_CONFIG.search.excludeSearchByLabel && FORM_CONFIG.search.excludeSearchByLabel.some(l => (question.label || '').toLowerCase().includes(l.toLowerCase()))) {
                 return acc;
             }

             if (!newNode.tipo) newNode.tipo = 'pregunta';
             if (newNode.id === undefined || newNode.id === null) {
               newNode.id = newNode.id_kobo_ficha_principal_pregunta || newNode.config_id_pregunta || `${newNode.name || 'pregunta'}_${newNode.indiceOriginal || 0}`;
             }

             acc.push(newNode);
        } else {
             // Keep unknown types just in case
             acc.push(newNode);
        }
        return acc;
      }, []);
    };

    return filterNodes(grupos);
  }, [questions]);

  const didApplyPreselectionRef = useRef(false);

  // Helper to get all question IDs in a node (group/subgroup)
  const getAllQuestionIds = (node) => {
    let ids = [];
    if (node.tipo === 'pregunta') {
      ids.push(node.id); // Assuming node.id is the question ID
    } else {
      if (node.preguntas) {
        node.preguntas.forEach(p => ids.push(p.id));
      }
      if (node.subgrupos) {
        node.subgrupos.forEach(sg => ids = ids.concat(getAllQuestionIds(sg)));
      }
      if (node.gruposHijos) {
        node.gruposHijos.forEach(gh => ids = ids.concat(getAllQuestionIds(gh)));
      }
    }
    return ids;
  };

  const isGroupMatch = (node, groupName, config) => {
    if (!node || !node.name) return false;

    const nodeName = config.caseSensitive ? node.name : String(node.name).toLowerCase();
    const compareWith = config.caseSensitive ? groupName : String(groupName).toLowerCase();

    if (config.exactMatch) {
      if (nodeName === compareWith) return true;
    } else {
      if (nodeName.includes(compareWith)) return true;
    }

    if (config.includeGroupPath && node.path) {
      const nodePath = config.caseSensitive ? node.path : String(node.path).toLowerCase();
      if (config.exactMatch) {
        return nodePath.includes(`/${compareWith}/`) || nodePath.endsWith(`/${compareWith}`);
      }
      return nodePath.includes(compareWith);
    }

    return false;
  };

  const isQuestionMatch = (node, questionName, config) => {
    if (!node) return false;
    const nodeQuestionName = node.preguntaOriginal?.name ?? node.name;
    if (!nodeQuestionName) return false;

    const nodeName = config.caseSensitive ? String(nodeQuestionName) : String(nodeQuestionName).toLowerCase();
    const compareWith = config.caseSensitive ? String(questionName) : String(questionName).toLowerCase();

    if (config.exactMatch) return nodeName === compareWith;
    return nodeName.includes(compareWith);
  };

  const getQuestionDisplayLabel = (node) => {
    const hasLabel = (value) => value !== null && value !== undefined && String(value).trim() !== '';
    if (hasLabel(node?.label)) return node.label;

    const questionName = node?.preguntaOriginal?.name ?? node?.name;
    if (!questionName) return '';

    return QUESTION_LABEL_OVERRIDES?.[questionName] || questionName;
  };

  useEffect(() => {
    if (didApplyPreselectionRef.current) return;
    if (!treeData || treeData.length === 0) return;

    const groupConfig = GROUP_PRESELECTION_CONFIG;
    const questionConfig = QUESTION_PRESELECTION_CONFIG;

    const hasGroupPreselection = Boolean(
      groupConfig && groupConfig.enabled && Array.isArray(groupConfig.groups) && groupConfig.groups.length > 0
    );
    const hasQuestionPreselection = Boolean(
      questionConfig && questionConfig.enabled && Array.isArray(questionConfig.names) && questionConfig.names.length > 0
    );

    if (!hasGroupPreselection && !hasQuestionPreselection) return;

    const idsToSelect = new Set();

    const visit = (node) => {
      if (!node) return;

      const isGroupNode = node.tipo === 'grupo' || node.tipo === 'subgrupo' || (node.preguntaOriginal?.type === 'begin_group');
      if (isGroupNode) {
        if (hasGroupPreselection) {
          const shouldSelect = groupConfig.groups.some((g) => isGroupMatch(node, g, groupConfig));
          if (shouldSelect) {
            getAllQuestionIds(node).forEach((id) => idsToSelect.add(id));
          }
        }
      }

      if (node.tipo === 'pregunta') {
        if (hasQuestionPreselection) {
          const shouldSelect = questionConfig.names.some((qName) => isQuestionMatch(node, qName, questionConfig));
          if (shouldSelect) idsToSelect.add(node.id);
        }
      }

      (node.gruposHijos || []).forEach(visit);
      (node.subgrupos || []).forEach(visit);
      (node.preguntas || []).forEach(visit);
    };

    treeData.forEach(visit);

    if (idsToSelect.size === 0) {
      didApplyPreselectionRef.current = true;
      return;
    }

    const merged = new Set(selectedQuestionIds);
    let changed = false;
    idsToSelect.forEach((id) => {
      if (!merged.has(id)) {
        merged.add(id);
        changed = true;
      }
    });

    didApplyPreselectionRef.current = true;
    if (changed) onSelectionChange(merged);
  }, [treeData, selectedQuestionIds, onSelectionChange]);


  // Check if all questions in a node are selected
  const isNodeSelected = (node) => {
    const ids = getAllQuestionIds(node);
    if (ids.length === 0) return false;
    return ids.every(id => selectedQuestionIds.has(id));
  };
  
  // Check if some (but not all) questions in a node are selected (indeterminate)
  const isNodeIndeterminate = (node) => {
    const ids = getAllQuestionIds(node);
    if (ids.length === 0) return false;
    const selectedCount = ids.filter(id => selectedQuestionIds.has(id)).length;
    return selectedCount > 0 && selectedCount < ids.length;
  };

  const handleNodeToggle = (node) => {
    const ids = getAllQuestionIds(node);
    const isSelected = isNodeSelected(node);
    
    // Create new Set
    const newSelected = new Set(selectedQuestionIds);
    
    if (isSelected) {
      // Deselect all
      ids.forEach(id => newSelected.delete(id));
    } else {
      // Select all
      ids.forEach(id => newSelected.add(id));
    }
    
    onSelectionChange(newSelected);
  };

  const handleSingleToggle = (id) => {
    const newSelected = new Set(selectedQuestionIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    onSelectionChange(newSelected);
  };

  // Recursive renderer
  const renderNode = (node) => {
    if (node.tipo === 'pregunta') {
       return (
         <div key={node.id} className="tree-node question-node">
            <input 
              type="checkbox" 
              checked={selectedQuestionIds.has(node.id)}
              onChange={() => handleSingleToggle(node.id)}
            />
            <span className="node-label" onClick={() => handleSingleToggle(node.id)}>
              {getQuestionDisplayLabel(node)}
            </span>
         </div>
       );
    }
    
    // Group or Subgroup
    return (
       <GroupNode 
         key={node.id || node.name} 
         node={node} 
         isSelected={isNodeSelected(node)}
         isIndeterminate={isNodeIndeterminate(node)}
         onToggle={() => handleNodeToggle(node)}
         renderChildren={renderNode}
       />
    );
  };

  if (!treeData.length) {
    return <div className="text-muted p-3">No hay preguntas disponibles o cargando...</div>;
  }

  return (
    <div className="question-selector-tree">
      {treeData.map(node => renderNode(node))}
    </div>
  );
};

const GroupNode = ({ node, isSelected, isIndeterminate, onToggle, renderChildren }) => {
  const [expanded, setExpanded] = useState(false);
  const checkboxRef = useRef();
  const groupLabel = GROUP_LABEL_OVERRIDES[node.name] || node.label || node.name;

  useEffect(() => {
    if (checkboxRef.current) {
      checkboxRef.current.indeterminate = isIndeterminate;
    }
  }, [isIndeterminate]);

  // Combine all children types
  const children = [
    ...(node.gruposHijos || []),
    ...(node.subgrupos || []),
    ...(node.preguntas || [])
  ];
  
  // Sort by original index to maintain form order
  const sortedChildren = children.sort((a, b) => (a.indiceOriginal || 0) - (b.indiceOriginal || 0));

  if (sortedChildren.length === 0) return null;

  return (
    <div className={`tree-node group-node type-${node.preguntaOriginal?.type || node.tipo || 'group'}`}>
       <div className="group-header">
         <button className="expand-btn" onClick={() => setExpanded(!expanded)}>
           {expanded ? '▼' : '▶'}
         </button>
         <input 
           type="checkbox" 
           checked={isSelected}
           ref={checkboxRef}
           onChange={onToggle}
         />
         <span className="node-label group-label" onClick={() => setExpanded(!expanded)}>
           {groupLabel}
         </span>
       </div>
       {expanded && (
         <div className="group-children">
           {sortedChildren.map(child => renderChildren(child))}
         </div>
       )}
    </div>
  );
};

export default QuestionSelectorTree;
